import React, { useState, useEffect } from 'react';
import { X, Play, ChevronRight, Sparkles, User, Package, Home, Filter } from 'lucide-react';
import { Button } from './ui/button';
import api from '../lib/api';

const CATEGORY_ICONS = {
  talking_head: User,
  large_product: Home,
  product_only: Package,
};

const CATEGORY_LABELS = {
  talking_head: "Talking Head",
  large_product: "Large Product",
  product_only: "Product Only",
};

const PERSONA_FLAGS = {
  american: "🇺🇸",
  british: "🇬🇧",
  french: "🇫🇷",
  african: "🇳🇬",
  ghanaian: "🇬🇭",
  indian: "🇮🇳",
  asian: "🇯🇵",
};

export default function TemplatePreviewGallery({ isOpen, onClose, onSelectTemplate }) {
  const [previews, setPreviews] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [selectedPreview, setSelectedPreview] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadPreviews();
    }
  }, [isOpen]);

  const loadPreviews = async () => {
    setLoading(true);
    try {
      const response = await api.get('/templates/previews');
      if (response.data.success) {
        setPreviews(response.data.previews);
        setGrouped(response.data.grouped);
      }
    } catch (error) {
      console.error('Failed to load template previews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUseTemplate = (preview) => {
    onSelectTemplate({
      persona: preview.persona,
      gender: preview.gender,
      emotion: preview.emotion,
      ageGroup: preview.age_group,
      videoMode: preview.category === 'large_product' ? 'large_product' : 
                 preview.category === 'product_only' ? 'product_only' : 'talking_head',
      largeProductCategory: preview.product_category || 'car',
      sampleScript: preview.sample_script,
    });
    onClose();
  };

  const filteredPreviews = selectedCategory 
    ? previews.filter(p => p.category === selectedCategory)
    : previews;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="template-gallery-modal">
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden border border-white/10 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white" data-testid="template-gallery-title">Template Preview Gallery</h2>
              <p className="text-white/60 text-sm">Preview sample videos before using credits</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            data-testid="template-gallery-close-btn"
          >
            <X className="w-6 h-6 text-white/70" />
          </button>
        </div>

        {/* Category Filter */}
        <div className="flex gap-2 p-4 border-b border-white/10 bg-white/5" data-testid="template-category-filters">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              !selectedCategory 
                ? 'bg-cyan-500 text-white' 
                : 'bg-white/10 text-white/70 hover:bg-white/20'
            }`}
            data-testid="template-filter-all"
          >
            <Filter className="w-4 h-4" />
            All Templates
          </button>
          {Object.entries(CATEGORY_LABELS).map(([key, label]) => {
            const Icon = CATEGORY_ICONS[key];
            const count = grouped[key]?.length || 0;
            return (
              <button
                key={key}
                onClick={() => setSelectedCategory(key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                  selectedCategory === key 
                    ? 'bg-cyan-500 text-white' 
                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
                data-testid={`template-filter-${key}`}
              >
                <Icon className="w-4 h-4" />
                {label}
                <span className="px-1.5 py-0.5 bg-white/20 rounded text-xs">{count}</span>
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]" data-testid="template-gallery-content">
          {loading ? (
            <div className="flex items-center justify-center py-20" data-testid="template-gallery-loading">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="template-grid">
              {filteredPreviews.map((preview) => (
                <div 
                  key={preview.id}
                  className="group bg-white/5 rounded-xl border border-white/10 overflow-hidden hover:border-cyan-500/50 transition-all hover:shadow-lg hover:shadow-cyan-500/10"
                  data-testid={`template-card-${preview.id}`}
                >
                  {/* Video Thumbnail / Player */}
                  <div className="relative aspect-[9/16] max-h-[280px] bg-black/50 overflow-hidden">
                    {playingVideo === preview.id ? (
                      <video
                        src={preview.preview_video_url}
                        autoPlay
                        controls
                        className="w-full h-full object-cover"
                        onEnded={() => setPlayingVideo(null)}
                      />
                    ) : (
                      <>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                        {preview.thumbnail_url ? (
                          <img 
                            src={preview.thumbnail_url} 
                            alt={preview.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-cyan-900/30 to-blue-900/30">
                            <Sparkles className="w-12 h-12 text-cyan-500/50" />
                          </div>
                        )}
                        <button
                          onClick={() => setPlayingVideo(preview.id)}
                          className="absolute inset-0 flex items-center justify-center group/play"
                        >
                          <div className="w-16 h-16 rounded-full bg-cyan-500/90 flex items-center justify-center transform group-hover/play:scale-110 transition-transform shadow-lg">
                            <Play className="w-8 h-8 text-white ml-1" />
                          </div>
                        </button>
                        {/* Duration Badge */}
                        <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/70 rounded text-xs text-white">
                          {preview.duration}s
                        </div>
                        {/* Category Badge */}
                        <div className="absolute top-2 left-2 px-2 py-1 bg-cyan-500/80 rounded text-xs text-white font-medium">
                          {CATEGORY_LABELS[preview.category]}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xl">{PERSONA_FLAGS[preview.persona] || "🌍"}</span>
                      <h3 className="text-white font-semibold text-sm">{preview.name}</h3>
                    </div>
                    <p className="text-white/60 text-xs mb-3 line-clamp-2">{preview.description}</p>
                    
                    {/* Ideal For Tags */}
                    <div className="flex flex-wrap gap-1 mb-3">
                      {preview.ideal_for?.slice(0, 3).map((tag, i) => (
                        <span key={i} className="px-2 py-0.5 bg-white/10 rounded text-xs text-white/70">
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Interactions (for large product) */}
                    {preview.interactions && (
                      <div className="mb-3 p-2 bg-white/5 rounded-lg">
                        <p className="text-xs text-cyan-400 font-medium mb-1">Interactions:</p>
                        <p className="text-xs text-white/60">{preview.interactions.join(" → ")}</p>
                      </div>
                    )}

                    {/* Sample Script */}
                    <div className="mb-3 p-2 bg-white/5 rounded-lg">
                      <p className="text-xs text-white/40 mb-1">Sample script:</p>
                      <p className="text-xs text-white/70 italic">"{preview.sample_script}"</p>
                    </div>

                    {/* Use Template Button */}
                    <Button
                      onClick={() => handleUseTemplate(preview)}
                      className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white text-sm"
                      data-testid={`use-template-btn-${preview.id}`}
                    >
                      Use This Template
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && filteredPreviews.length === 0 && (
            <div className="text-center py-20">
              <Sparkles className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <p className="text-white/60">No templates found for this filter</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/10 bg-white/5">
          <p className="text-center text-white/50 text-sm">
            💡 <span className="text-cyan-400">Tip:</span> Preview videos are free! 
            Select a template to pre-fill your settings and customize further.
          </p>
        </div>
      </div>
    </div>
  );
}
